/* ──────────────────────────────────────────────────────────────────────────
 * InQAI Intelligence Router — §18.
 *
 * The brief's instruction is blunt and correct:
 *
 *   "Don't force everything through an LLM. Use the correct computational
 *    paradigm."
 *
 * So this is a dispatcher, not a wrapper. A task is classified, matched to
 * the paradigm that should handle it, and routed to the engine in this
 * codebase that actually implements it. Crowd forecasting goes to the
 * time-series model. Intervention comparison goes to the simulator. Spatial
 * questions go to the GIS layer. Nothing is sent to a language model
 * because a language model was the only thing available.
 *
 * ── THE PART THAT MAKES IT HONEST ──
 * Two paradigms in the brief's list have no engine behind them here:
 * computer vision (there is no video pipeline) and RAG over a document
 * corpus (there is no approved SOP library loaded). The router routes to
 * them correctly and then reports UNAVAILABLE with the reason.
 *
 * That matters more than it looks. A router that silently fell back to a
 * language model for a CCTV question would produce a fluent answer about
 * a camera it cannot see. Refusing is the only safe behaviour, and the
 * refusal names what would need to be integrated to change it.
 *
 * "Quantum-inspired" appears in the brief with the qualifier "where
 * justified". Nothing here justifies it, so the paradigm is declared,
 * marked unavailable, and the note says plainly that no problem in this
 * platform currently needs it. Claiming otherwise would be the easiest
 * and least defensible thing in the document to fake.
 * ────────────────────────────────────────────────────────────────────────── */

import { forecast, HORIZONS } from "../geo/services/liveModel";
import { simulateInterventions } from "../geo/services/interventions";
import { buildResponsePlan } from "../geo/services/emergencyResponse";
import { assessMobilityChain } from "../geo/services/mobilityChain";
import { correlateHazards } from "../geo/services/multiHazard";
import { assessServiceStress } from "../geo/services/serviceStress";
import { buildTwin } from "../twin/twin";
import { searchSOP, SOP_LIBRARY } from "./sop";

/* ── Paradigms ─────────────────────────────────────────────────────────
 * The brief's list, verbatim, so coverage is checkable line by line. */
export const PARADIGMS = {
  cv: {
    id: "cv",
    label: { en: "Computer Vision", mr: "संगणक दृष्टी" },
    handles: "CCTV and video analytics",
  },
  timeseries: {
    id: "timeseries",
    label: { en: "Time-series / ML", mr: "कालमालिका / एमएल" },
    handles: "Crowd forecasting",
  },
  graph: {
    id: "graph",
    label: { en: "Graph analytics", mr: "आलेख विश्लेषण" },
    handles: "Network movement and dependency",
  },
  rag: {
    id: "rag",
    label: { en: "RAG / LLM", mr: "दस्तऐवज शोध" },
    handles: "SOP and document questions",
  },
  optimisation: {
    id: "optimisation",
    label: { en: "Optimisation", mr: "इष्टतमीकरण" },
    handles: "Resource allocation",
  },
  simulation: {
    id: "simulation",
    label: { en: "Simulation", mr: "सिम्युलेशन" },
    handles: "Intervention comparison",
  },
  gis: {
    id: "gis",
    label: { en: "GIS", mr: "भौगोलिक माहिती" },
    handles: "Spatial operations",
  },
  workflow: {
    id: "workflow",
    label: { en: "Agents / workflow", mr: "कार्यप्रवाह" },
    handles: "Cross-agency orchestration",
  },
  quantum: {
    id: "quantum",
    label: { en: "Quantum-inspired / hybrid", mr: "क्वांटम-प्रेरित" },
    handles: "Large combinatorial optimisation, where justified",
  },
};

/* ── Task kinds ────────────────────────────────────────────────────────
 * A task is classified by kind rather than by parsing free text, because a
 * router that guesses from prose will eventually guess wrong about
 * something that matters. §13's copilot does the prose→kind step and shows
 * the operator which kind it chose. */
export const ROUTES = {
  crowd_forecast: {
    paradigm: "timeseries",
    engine: "liveModel.forecast",
    available: true,
    run: ({ liveState, horizons = HORIZONS }) => forecast(liveState, horizons),
  },
  intervention_compare: {
    paradigm: "simulation",
    engine: "interventions.simulateInterventions",
    available: true,
    run: ({ liveState, sector, twin }) => simulateInterventions(liveState, sector, { twin }),
  },
  emergency_dispatch: {
    paradigm: "optimisation",
    engine: "emergencyResponse.buildResponsePlan",
    available: true,
    run: ({ liveState, incident, twin }) => buildResponsePlan(incident, liveState, { twin }),
  },
  resource_allocation: {
    paradigm: "optimisation",
    engine: "emergencyResponse.rankResponders",
    available: true,
    run: ({ liveState, incident, twin }) => buildResponsePlan(incident, liveState, { twin }),
  },
  network_movement: {
    paradigm: "graph",
    engine: "mobilityChain.assessMobilityChain",
    available: true,
    run: ({ liveState, disruptions, twin }) =>
      assessMobilityChain(liveState, { disruptions, twin }),
  },
  dependency_impact: {
    paradigm: "graph",
    engine: "twin.impactOf",
    available: true,
    run: ({ liveState, entityId, twin }) =>
      (twin ?? buildTwin(liveState)).impactOf(entityId),
  },
  spatial_query: {
    paradigm: "gis",
    engine: "twin.inSector / geojson",
    available: true,
    run: ({ liveState, sector, twin }) =>
      (twin ?? buildTwin(liveState)).inSector(sector),
  },
  hazard_correlation: {
    paradigm: "graph",
    engine: "multiHazard.correlateHazards",
    available: true,
    run: ({ liveState, context }) => correlateHazards(liveState, context ?? {}),
  },
  service_forecast: {
    paradigm: "timeseries",
    engine: "serviceStress.assessServiceStress",
    available: true,
    run: ({ liveState }) => assessServiceStress(liveState),
  },
  sop_lookup: {
    paradigm: "rag",
    engine: "sop.searchSOP",
    available: true,
    /* Deterministic retrieval over a small curated set, NOT a language
     * model. It returns the document text and its reference, and never
     * paraphrases — see sop.js for why. */
    run: ({ query }) => searchSOP(query),
  },
  video_analytics: {
    paradigm: "cv",
    engine: null,
    available: false,
    reason:
      "No video pipeline is integrated. Camera positions exist in the twin but no feed is attached, so nothing can be inferred from video. Answering this would require integrating the ICCC video analytics stream.",
  },
  face_or_identity: {
    paradigm: "cv",
    engine: null,
    available: false,
    /* Refused on policy as well as capability — see §17. */
    reason:
      "Identity analytics is out of scope by design. The platform prioritises anonymous, aggregate crowd analytics; per-person identification is not implemented and would require a separate legal basis.",
    refusedOnPolicy: true,
  },
  cross_agency_workflow: {
    paradigm: "workflow",
    engine: "spine.incidentMachine",
    available: true,
    run: ({ incident }) => ({
      note: "Routed to the incident state machine. Transitions require an authorised officer.",
      incident,
    }),
  },
  combinatorial_optimisation: {
    paradigm: "quantum",
    engine: null,
    available: false,
    reason:
      "No problem in this platform currently justifies quantum-inspired methods. Present resource-allocation problems are small enough to solve exactly. This route exists so the claim is never made without a problem behind it.",
  },
};

/* ── Routing ───────────────────────────────────────────────────────────── */
export function route(kind) {
  const r = ROUTES[kind];
  if (!r) {
    return {
      kind,
      routed: false,
      reason: `No route is defined for task kind "${kind}". The router does not guess.`,
    };
  }
  return {
    kind,
    routed: true,
    paradigm: PARADIGMS[r.paradigm],
    engine: r.engine,
    available: r.available,
    reason: r.reason ?? null,
    refusedOnPolicy: r.refusedOnPolicy ?? false,
  };
}

/* Execute a routed task. Returns the engine's result, or an explicit
 * unavailable envelope. It never falls back to a different paradigm —
 * a fluent answer from the wrong engine is worse than no answer. */
export function execute(kind, args = {}) {
  const decision = route(kind);
  if (!decision.routed) {
    return { ok: false, ...decision };
  }
  const r = ROUTES[kind];
  if (!r.available) {
    return {
      ok: false,
      ...decision,
      unavailable: true,
      /* Stated explicitly so no caller can mistake this for a null result. */
      note: "The router identified the correct paradigm and declined to substitute another.",
    };
  }
  return { ok: true, ...decision, result: r.run(args) };
}

/* ── Coverage ──────────────────────────────────────────────────────────
 * What the platform can and cannot do, by paradigm. §16 surfaces this. */
export function routerCoverage() {
  const byParadigm = Object.values(PARADIGMS).map((p) => {
    const routes = Object.entries(ROUTES).filter(([, r]) => r.paradigm === p.id);
    const available = routes.filter(([, r]) => r.available);
    return {
      paradigm: p,
      routes: routes.length,
      available: available.length,
      /* A paradigm is "attached" when at least one of its routes has a
       * real engine behind it. */
      attached: available.length > 0,
      gaps: routes
        .filter(([, r]) => !r.available)
        .map(([kind, r]) => ({ kind, reason: r.reason })),
    };
  });

  const attached = byParadigm.filter((p) => p.attached).length;
  return {
    byParadigm,
    paradigmCount: byParadigm.length,
    attached,
    statement:
      `${attached} of ${byParadigm.length} computational paradigms have an engine attached in this platform. `
      + `The rest are routed correctly and report why they cannot answer, rather than being substituted with a language model.`,
    sopDocuments: SOP_LIBRARY.length,
  };
}

export const ROUTER_ASSUMPTIONS = [
  "Tasks are routed by declared kind, not by parsing free text. A router that guesses from prose will eventually guess wrong about something that matters.",
  "There is no fallback between paradigms. A CCTV question is never answered by a language model, because a fluent answer about a camera the platform cannot see is worse than no answer.",
  "Computer vision is routed but unavailable: camera entities exist in the twin, no video feed is attached.",
  "SOP lookup is deterministic keyword retrieval over a small curated set, not a generative model. It returns source text and a reference and never paraphrases.",
  "Quantum-inspired methods are declared and unavailable. No problem in this platform is large enough to justify them, and the route exists so the claim is never made without a problem behind it.",
  "Identity analytics is refused on policy, not only capability. The platform is built for anonymous aggregate analytics.",
];
